import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Camera, Trash } from '@styled-icons/boxicons-regular';
import { ReloadCircle, Duplicate, PauseCircle } from '@styled-icons/ionicons-outline';
import { formatPrice } from 'utils/price';
import { GET_ORDERS_OF_TOTEM } from 'data/queries';
import { ARCHIVED, CREATED, INPROGRESS, PAUSED, READY, VALIDATED } from 'constants/states-app';
import { FormattedDeliveryDate } from 'pages/Totem/TotemDashboard/FormattedDeliveryDate';
import { MeatBallsMenu } from 'components/MeatBallsMenu';
import { MeatBallsListItem } from 'components/MeatBallsListItem';
import { OrderTableState } from 'pages/Totem/TotemDashboard/OrderTableState';
import { TotemDeliveryPicturesPopUp } from './TotemDeliveryPicturesPopUp';
import { DuplicateOrderPopUp } from 'pages/Order/OrderDuplicate/DuplicateOrderPopUp';
import { PauseOrderPopUp } from 'pages/Order/OrderPausing/PauseOrderPopUp';
import { TotemUserPicture } from 'pages/Totem/TotemDashboard/TotemUserPicture';
import { getUrlParameters } from 'utils';
import { getDeliveryHourRangeString } from 'utils/time';
import { PUNCTUAL, RECURRING } from 'constants/types';
import { PunctualIcon, RecurringIcon, MagnifyingGlassIcon } from 'styles/pages/totem.styles';
import { InvoicesTableStyle } from 'styles/pages/settings.styles';
import { DashboardOrderModifyButton } from 'pages/Totem/TotemDashboard/DashboardOrderModifyButton';
import { analyticsEvents, analyticsPropertiesConstants, analyticsSourceConstants, track } from 'tracking/segment';
import { calculateOrderPrices } from 'pages/Order/OrderDetails/utils/calculateOrderPrices';
import { mergeProductsAndProductsRequested } from 'pages/Order/OrderDetails/utils/mergeProductsAndProductsRequested';
import { DashboardOrderHistoryProps } from 'pages/Totem/TotemDashboard/TotemDashboard.types';
import {
    GetArchivedOrdersQuery,
    ProductFlow,
    useDeleteOrderMutation,
    useReactivateOrderMutation,
} from 'data/__generated__';

export type MinimalArchivedOrder = GetArchivedOrdersQuery['archivedOrders']['archivedOrders'][number] | null;

function getFrequencyName(frequency: string) {
    switch (frequency) {
        case 'BiWeekly':
            return 'Bi-mensuelle';
        case 'Monthly':
            return 'Mensuelle';
        case 'Quarterly':
            return 'Trimestrielle';
        case 'Weekly':
            return 'Hebdomadaire';
        case 'BiAnnually':
            return 'Semestrielle';
        default:
            return 'récurrente';
    }
}
export interface OrderHistoryTableProps extends Pick<DashboardOrderHistoryProps, 'ordersOfTotem' | 'baseUrl'> {
    defaultCategoryId?: string;
    ordersToShow: MinimalArchivedOrder[];
    nbOrderToShow: number;
}
export const OrderHistoryTable: React.FC<OrderHistoryTableProps> = ({
    ordersToShow,
    nbOrderToShow = 20,
    ordersOfTotem,
    defaultCategoryId,
    baseUrl,
}) => {
    const [isShowingPhotos, setShowPhotos] = useState(false);
    const [isDuplicatingOrder, setIsDuplicatingOrder] = useState(false);
    const [orderBeingDuplicated, setOrderBeingDuplicated] = useState<MinimalArchivedOrder | null>(null);
    const [isPausingOrder, setIsPausingOrder] = useState(false);
    const [orderBeingPaused, setOrderBeingPaused] = useState<MinimalArchivedOrder | null>(null);
    const [photos, setPhotos] = useState<string[] | null>(null);
    // props
    const { totemId } = getUrlParameters(window.location.pathname);
    const { recentOrders } = ordersOfTotem ?? {};
    // hooks
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    // queries and mutations
    const [reactivateOrder, { loading: reactivateLoading }] = useReactivateOrderMutation();
    const [deleteOrder] = useDeleteOrderMutation();

    // functions
    const handleSeePhotos = (photos: string[]) => {
        setShowPhotos(true);
        setPhotos(photos);
        track(analyticsEvents.SEE_ORDER_PHOTOS);
    };

    const goToOrder =
        ({ orderId }: { orderId: string }) =>
        async () => {
            track(analyticsEvents.MODIFY_ORDER, {
                [analyticsPropertiesConstants.COMMON.SOURCE]: analyticsSourceConstants.DASHBOARD,
            });
            navigate(`${baseUrl}/${defaultCategoryId}/${orderId}`);
        };

    const handleReactivateOrder = async (orderId: string) => {
        if (reactivateLoading) return;

        const { data } = await reactivateOrder({
            variables: { orderId },
        });
        const updatedOrder = data?.reactivateOrder;

        const updatedOrders = recentOrders?.map((orderFromCache: any) =>
            orderFromCache._id === orderId ? updatedOrder : orderFromCache,
        );

        apolloClient.writeQuery({
            query: GET_ORDERS_OF_TOTEM,
            variables: { totemId },
            data: {
                ordersOfTotem: { ...ordersOfTotem, recentOrders: updatedOrders },
            },
        });
        track(analyticsEvents.REACTIVATE_ORDER);
    };

    const handleDeleteOrder = (orderId: string) => async () => {
        await deleteOrder({ variables: { orderId } });
        const recentOrders = ordersOfTotem?.recentOrders ?? [];
        const updatedOrders = [...recentOrders];
        const orderIds = updatedOrders.map((localOrder) => localOrder?._id);
        const orderIndex = orderIds.indexOf(orderId);

        if (orderIndex >= 0) {
            updatedOrders.splice(orderIndex, 1);
        }

        apolloClient.writeQuery({
            query: GET_ORDERS_OF_TOTEM,
            variables: { totemId },
            data: {
                ordersOfTotem: { ...ordersOfTotem, recentOrders: updatedOrders },
            },
        });
        track(analyticsEvents.DELETE_ORDER);
    };

    const handleDuplicateOrder = (order: MinimalArchivedOrder) => {
        setIsDuplicatingOrder(true);
        setOrderBeingDuplicated(order);
        track(analyticsEvents.START_ORDER_DUPLICATION);
    };

    const handlePauseOrder = (order: MinimalArchivedOrder) => {
        setIsPausingOrder(true);
        setOrderBeingPaused(order);
        track(analyticsEvents.START_ORDER_PAUSING);
    };

    const getOrderPhotos = (order: MinimalArchivedOrder): string[] => {
        const orderPhotos: string[] = [];

        order?.delivery?.photos?.forEach((photo) => {
            if (photo.urls?.length) {
                orderPhotos.push(...photo.urls);
            }
        });

        // A delivery with no site has no SiteSetup: its freefood rangement hangs on the delivery itself.
        const instructions = (order?.delivery?.siteSetup?.freefoodSetup ?? order?.delivery?.freefoodSetup)
            ?.instructions;
        const photoInstruction = instructions?.find((inst) => inst?.__typename === 'PhotosInstruction');
        if (photoInstruction?.__typename === 'PhotosInstruction' && 'photos' in photoInstruction) {
            (photoInstruction as any).photos?.forEach((photo: { urls?: string[] }) => {
                if (photo.urls?.length) {
                    orderPhotos.push(...photo.urls);
                }
            });
        }

        return orderPhotos;
    };

    return (
        <>
            <InvoicesTableStyle $spacing $marginMultiplier={[0, 0, 1.5, 0]}>
                <thead>
                    <tr>
                        <th>Utilisateur</th>
                        <th>N° de commande</th>
                        <th>Date et heures de livraison</th>
                        <th>Type de commande</th>
                        <th>Total HT</th>
                        <th>Statut</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {ordersToShow
                        // Livraison en cours en tête, le reste dans l'ordre reçu (le serveur trie par date
                        // de livraison décroissante). Le comparateur ne lisait que son premier argument :
                        // incohérent, donc l'ordre dépendait de l'algorithme de tri du moteur. `slice()`
                        // d'abord car `sort` mute, et la liste appartient à l'appelant.
                        ?.slice()
                        .sort(
                            (order1, order2) =>
                                Number(order2?.delivery?.state === INPROGRESS) -
                                Number(order1?.delivery?.state === INPROGRESS),
                        )
                        .slice(0, nbOrderToShow)
                        .map((order) => {
                            const {
                                _id: orderId,
                                date_delivery,
                                deliveryTimeWindows,
                                isPunctual,
                                frequency,
                                state,
                                first_order: firstOrder,
                                productsWithQuantityPicked,
                                productsRequestedButNotFullyDelivered,
                                stateHistory,
                                delivery,
                            } = order ?? {
                                _id: '',
                                date_delivery: '',
                                deliveryTimeWindows: [],
                                isPunctual: false,
                                frequency: '',
                                state: 'Created',
                                first_order: false,
                                productsWithQuantityPicked: [],
                                productsRequestedButNotFullyDelivered: [],
                                stateHistory: [],
                                delivery: null,
                            };

                            const orderPhotos = getOrderPhotos(order);
                            const stateRecordHasBeenValidated = stateHistory?.some(({ state }) => state === VALIDATED);
                            const currentOrders = recentOrders?.filter((order) => order?.state !== ARCHIVED);
                            const isAdditionalWeekdayOnboardingOrder =
                                !isPunctual && firstOrder && currentOrders?.length;

                            const productsWithQuantityRequested = mergeProductsAndProductsRequested({
                                products: productsWithQuantityPicked ?? [],
                                productRequestedWithQuantities: productsRequestedButNotFullyDelivered ?? [],
                                deliveryDate: order?.date_delivery ?? '',
                            });

                            const { allProductPriceWithDelivery } = calculateOrderPrices({
                                products: productsWithQuantityRequested,
                                priceDelivery: order?.prices?.priceDelivery ?? 0,
                            });

                            const orderTestType = isPunctual ? PUNCTUAL : `${RECURRING}-${state?.toLowerCase()}`;
                            return (
                                <tr key={orderId}>
                                    <td width="10%">
                                        <TotemUserPicture profile={order?.user?.profile} />
                                    </td>
                                    <td width="10%">N°{order?.number}</td>
                                    <td width="20%">
                                        <FormattedDeliveryDate deliveryDate={date_delivery} />,
                                        <br />
                                        {getDeliveryHourRangeString(
                                            deliveryTimeWindows?.[0].start,
                                            deliveryTimeWindows?.[0].stop,
                                        )}
                                    </td>
                                    {isPunctual ? (
                                        <td width="20%">
                                            <PunctualIcon size="20px" /> Ponctuelle
                                        </td>
                                    ) : (
                                        <td width="20%">
                                            <RecurringIcon size="20px" /> {getFrequencyName(frequency as string)}
                                        </td>
                                    )}
                                    <td width="15%">{formatPrice(allProductPriceWithDelivery)}</td>
                                    <td width="20%">
                                        {' '}
                                        <OrderTableState
                                            delivery={delivery}
                                            firstOrder={firstOrder}
                                            hasBeenValidated={stateRecordHasBeenValidated}
                                            isPunctual={isPunctual}
                                            state={state}
                                        />
                                    </td>
                                    <td width="25%">
                                        <LastColumn>
                                            <MeatBallsMenu dataTest="order-action-button">
                                                {[ARCHIVED, READY].includes(state) && (
                                                    <MeatBallsListItem
                                                        onClick={() => navigate(`/order/${orderId}`)}
                                                        icon={<MagnifyingGlassIcon color="secondary" size="14px" />}
                                                        label="Détails"
                                                    />
                                                )}
                                                {state === PAUSED && (
                                                    <MeatBallsListItem
                                                        onClick={() => handleReactivateOrder(orderId)}
                                                        icon={<ReloadCircle color="secondary" size="14px" />}
                                                        label="Réactiver ma commande"
                                                        loading={reactivateLoading}
                                                    />
                                                )}
                                                {!isPunctual &&
                                                    ![PAUSED, ARCHIVED, READY].includes(state) &&
                                                    !(
                                                        order?.catererLocked &&
                                                        order?.products?.some((p) => p.flow === ProductFlow.Caterer)
                                                    ) && (
                                                        <MeatBallsListItem
                                                            onClick={() => handlePauseOrder(order)}
                                                            icon={<PauseCircle color="secondary" size="14px" />}
                                                            label="Suspendre"
                                                        />
                                                    )}
                                                {![ARCHIVED, READY].includes(state) &&
                                                    (isPunctual || isAdditionalWeekdayOnboardingOrder) &&
                                                    [CREATED, INPROGRESS].includes(state) &&
                                                    !stateRecordHasBeenValidated &&
                                                    !(
                                                        order?.catererLocked &&
                                                        order?.products?.some((p) => p.flow === ProductFlow.Caterer)
                                                    ) && (
                                                        <MeatBallsListItem
                                                            onClick={handleDeleteOrder(orderId)}
                                                            icon={<Trash color="secondary" size="14px" />}
                                                            label="Supprimer"
                                                        />
                                                    )}
                                                {orderPhotos.length ? (
                                                    <MeatBallsListItem
                                                        onClick={() => handleSeePhotos(orderPhotos)}
                                                        icon={<Camera color="secondary" size="14px" />}
                                                        label="Voir les photos"
                                                    />
                                                ) : null}
                                                <MeatBallsListItem
                                                    onClick={() => handleDuplicateOrder(order)}
                                                    icon={<Duplicate color="secondary" size="14px" />}
                                                    label="Dupliquer"
                                                    dataTest="duplicate-order-button"
                                                />
                                            </MeatBallsMenu>
                                            {![ARCHIVED, READY, PAUSED].includes(state) && (
                                                <DashboardOrderModifyButton
                                                    orderTestType={orderTestType}
                                                    onClick={goToOrder({
                                                        orderId,
                                                    })}
                                                    deliveryDate={date_delivery ?? ''}
                                                    firstOrder={firstOrder === undefined ? null : firstOrder}
                                                    hasBeenValidated={stateRecordHasBeenValidated}
                                                    isPunctual={isPunctual}
                                                    state={state ?? 'Created'}
                                                />
                                            )}
                                        </LastColumn>
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </InvoicesTableStyle>
            {isShowingPhotos && (
                <TotemDeliveryPicturesPopUp
                    photos={photos ? photos.map((url) => ({ urls: [url] })) : null}
                    setShowPictures={setShowPhotos}
                />
            )}
            {isDuplicatingOrder && (
                <DuplicateOrderPopUp
                    order={orderBeingDuplicated}
                    closeOrderPopup={() => setIsDuplicatingOrder(false)}
                    defaultCategoryId={defaultCategoryId}
                />
            )}
            {isPausingOrder && orderBeingPaused && (
                <PauseOrderPopUp order={orderBeingPaused} closePopup={() => setIsPausingOrder(false)} />
            )}
        </>
    );
};

const LastColumn = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
`;
